import { useState } from 'react';
import { X, Lightbulb } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import './SetupPageHero.css';

// ═══════════════════════════════════════════════
// SetupPageHero — Hero guia premium por pagina
// Aparece durante o setup inicial, desaparece
// apos primeiro cadastro ou dismiss manual
// ═══════════════════════════════════════════════

interface HeroAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

interface SetupPageHeroProps {
  /** Icone lucide para o titulo */
  icon: LucideIcon;
  /** Numero da etapa (ex: 2) */
  etapa: number;
  /** Total de etapas (ex: 7) */
  totalEtapas?: number;
  /** Titulo principal (ex: "Turnos Operacionais") */
  titulo: string;
  /** Descricao explicativa do que e a pagina */
  descricao: string;
  /** Dica pratica (ex: "A maioria das escolas tem 2 turnos") */
  dica?: string;
  /** Botoes de acao */
  acoes?: HeroAction[];
  /** Se true, aparece como bloqueado (cinza) */
  bloqueado?: boolean;
  /** Mensagem de bloqueio */
  mensagemBloqueio?: string;
  /** Se pode ser fechado pelo usuario */
  dismissivel?: boolean;
  /** Chave para persistir dismiss em localStorage */
  storageKey?: string;
  /** Modo compacto para reduzir altura e densidade visual */
  compact?: boolean;
}

export function SetupPageHero({
  icon: Icon,
  etapa,
  totalEtapas = 7,
  titulo,
  descricao,
  dica,
  acoes = [],
  bloqueado = false,
  mensagemBloqueio,
  dismissivel = false,
  storageKey,
  compact = true,
}: SetupPageHeroProps) {
  const [dismissed, setDismissed] = useState(() => {
    if (!storageKey) return false;
    return localStorage.getItem(storageKey) === '1';
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    if (storageKey) localStorage.setItem(storageKey, '1');
  };

  const dicaCompacta = (() => {
    if (!dica) return undefined;
    if (!compact) return dica;
    // Em modo compacto extremo, oculta dica para reduzir altura do card.
    return undefined;
  })();

  return (
    <div className={`setup-hero ${compact ? 'setup-hero-compact' : ''} ${bloqueado ? 'setup-hero-blocked' : ''}`}>
      {/* Oculto no mobile, visivel no desktop */}
      <div className="setup-hero-icon-wrapper setup-hero-hidden-mobile">
        <Icon size={32} className="setup-hero-icon" />
      </div>

      <div className="setup-hero-content">
        {/* Agrupamento da linha do cabeçalho no mobile */}
        <div className="setup-hero-header-group">
          <div className="setup-hero-icon-wrapper setup-hero-hidden-desktop">
            <Icon size={24} className="setup-hero-icon" />
          </div>
          
          <div className="setup-hero-title-group">
            <h2 className="setup-hero-title">
              {bloqueado ? mensagemBloqueio || titulo : titulo}
            </h2>
          </div>

          {dismissivel && (
            <button className="setup-hero-close" onClick={handleDismiss} title="Fechar guia">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Descricao */}
        <p className="setup-hero-desc">{descricao}</p>

        {/* Dica */}
        {dicaCompacta && (
          <div className="setup-hero-dica">
            <Lightbulb size={16} className="setup-hero-dica-icon" />
            <span>{dicaCompacta}</span>
          </div>
        )}

        {/* Acoes (so quando houver botoes customizados) */}
        {acoes.length > 0 && (
          <div className="setup-hero-actions">
            {acoes.map((acao, i) => (
              <button
                key={i}
                className={acao.variant === 'secondary' ? 'setup-hero-btn-secondary' : 'setup-hero-btn-primary'}
                onClick={acao.onClick}
              >
                {acao.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
